import * as React from "react";
import { useState } from "react";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Skeleton } from "antd5";
import { Flex, Text } from "styles/utility-components";

import { useBidQuestions } from "lib/hooks/api/bid/bid_question/useBidQuestions";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routeSegments } from "lib/routes";
import { BidReportType } from "lib/types/graphQLEnums";
import BidQuestionsForm from "./BidQuestionsForm";

export const BID_SELF_SERVE_CHANGED = "BID_SELF_SERVE_CHANGED";

function BidSelfServe(): JSX.Element {
  const { data: allQuestions, isLoading } = useBidQuestions(BidReportType.RequirementSummary);

  const [hasChanged, setHasChanged] = useState(false);
  // The user has navigated to the self-serve page from a requirement summary page
  const fromRequirementSummary =
    window.location.href.includes(routeSegments.requirementSummary) ||
    window.location.href.includes("bids");

  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <Page>
      <Header fromRequirementSummary={fromRequirementSummary}>
        {fromRequirementSummary && (
          <BackLink
            onClick={() => {
              if (hasChanged) {
                sessionStorage.setItem(BID_SELF_SERVE_CHANGED, "true");
                setHasChanged(false);
              }
              void navigate({
                to: pathname.replace(
                  /\/bid-self-serve\/?$/,
                  window.location.href.includes(routeSegments.requirementSummary)
                    ? ""
                    : `/${routeSegments.requirementSummary}`,
                ),
              });
            }}
          >
            <UIcon icon="arrowNarrowLeft" /> Back to requirement summary
          </BackLink>
        )}
        <Text h1>Requirement summary</Text>
        <Description>
          We generate a requirement summary based on default criteria when you upload a tender
          document. You can manage these criteria below.
          <ListDescription>On this page you can:</ListDescription>
          <List>
            <li>Add new criteria</li>
            <li>Edit, reorder, or remove any criteria</li>
          </List>
        </Description>
      </Header>
      {isLoading || !allQuestions ? (
        <SkeletonContainer>
          <Skeleton active />
          <Skeleton active />
        </SkeletonContainer>
      ) : (
        <BidQuestionsForm
          defaultValues={allQuestions.groups}
          fromRequirementSummary={fromRequirementSummary}
          hasChanged={hasChanged}
          setHasChanged={setHasChanged}
        />
      )}
    </Page>
  );
}

const Header = styled(Flex)<{ fromRequirementSummary: boolean }>(({ fromRequirementSummary }) => ({
  flexDirection: "column",
  gap: 24,
  padding: fromRequirementSummary ? 48 : 24,
  paddingTop: 24,
  paddingBottom: 0,
}));

const BackLink = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  cursor: "pointer",
  gap: 4,
  color: theme.colors.sysPrimaryDefault,
  "&:hover": {
    textDecoration: "underline",
    color: theme.colors.sysPrimaryDefault,
  },
}));

const Page = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 24,
  overflow: "auto",
  height: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
  width: "-webkit-fill-available",
}));

const Description = styled(Text)({
  maxWidth: 650,
});

const ListDescription = styled(Text)({
  marginTop: 16,
});

const List = styled.ul({
  fontWeight: 500,
  paddingInlineStart: 24,
  marginBlockStart: 8,
});

const SkeletonContainer = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  padding: 16,
  maxWidth: 968,
});

export default BidSelfServe;
